import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { SearchIcon } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';

/**
 * SearchTypeahead — Component (L1)
 *
 * Inline typeahead search field with a results dropdown that opens as
 * soon as the user types. Wraps shadcn `Command` (cmdk) and themes the
 * primitive to Signal DS tokens.
 *
 * Unlike `DropdownWithUsecase` this is a *search-first* surface: there's
 * no trigger button — the input itself is the affordance, and the dropdown
 * collapses when the input loses focus or is cleared. Used for global
 * search, patient lookup, code search, and any "type a few chars to find"
 * flow.
 *
 * Loader contract:
 *   fetchResults(query: string) => Promise<TypeaheadResult<T>[]>
 *
 * - Debounced by `debounceMs` (default 250)
 * - Stale-result drop via ref-counted request ids
 * - Empty query → no fetch, dropdown closed
 *
 * Tokens (NOT shadcn defaults):
 *   input       border-grey-300 / text-grey-700 / placeholder text-grey-600
 *   focus       outline blue-500
 *   selected    bg-blue-100 / text-blue-700
 *
 * a11y: native combobox semantics inherited from cmdk; `ariaLabel`
 * required on the wrapper.
 *
 * Scope: cross-module · Surfaces: practice · practitioner · patient
 * Contract: scrips-signal-ds/contracts/SearchTypeahead.json
 */

export interface TypeaheadResult<T = string> {
  value: T;
  label: string;
  description?: string;
}

export interface SearchTypeaheadProps<T = string> {
  fetchResults: (query: string) => Promise<TypeaheadResult<T>[]>;
  onSelect: (result: TypeaheadResult<T>) => void;
  /** Required aria-label for the search field. */
  ariaLabel: string;
  placeholder?: string;
  emptyMessage?: ReactNode;
  loadingMessage?: ReactNode;
  /** Debounce in ms before re-firing the loader. Default 250. */
  debounceMs?: number;
  /** Min query length before the loader fires. Default 1. */
  minLength?: number;
  /** Custom row renderer. Defaults to label + description. */
  renderResult?: (result: TypeaheadResult<T>) => ReactNode;
  /** Initial query value (uncontrolled hint). */
  initialQuery?: string;
  className?: string;
}

export function SearchTypeahead<T extends string | number = string>({
  fetchResults,
  onSelect,
  ariaLabel,
  placeholder = 'Search',
  emptyMessage = 'No results',
  loadingMessage = 'Searching…',
  debounceMs = 250,
  minLength = 1,
  renderResult,
  initialQuery = '',
  className,
}: SearchTypeaheadProps<T>) {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<TypeaheadResult<T>[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const reqIdRef = useRef(0);
  const wrapperId = useId();

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < minLength) {
      setResults([]);
      setLoading(false);
      setOpen(false);
      return;
    }
    setOpen(true);
    const t = window.setTimeout(() => {
      const myReq = ++reqIdRef.current;
      setLoading(true);
      void fetchResults(trimmed)
        .then((next) => {
          if (myReq !== reqIdRef.current) return;
          setResults(next);
        })
        .finally(() => {
          if (myReq !== reqIdRef.current) return;
          setLoading(false);
        });
    }, debounceMs);
    return () => window.clearTimeout(t);
  }, [query, fetchResults, debounceMs, minLength]);

  return (
    <div
      id={wrapperId}
      aria-label={ariaLabel}
      className={cn('relative w-full', className)}
    >
      <Command shouldFilter={false} className="border border-grey-300 rounded-md bg-white">
        <CommandInput
          placeholder={placeholder}
          value={query}
          onValueChange={setQuery}
        />
        {open && (
          <CommandList className="max-h-72 border-t border-grey-300">
            {loading && (
              <div
                role="status"
                aria-live="polite"
                className="px-3 py-4 text-center text-sm text-grey-600 flex items-center justify-center gap-2"
              >
                <SearchIcon className="h-4 w-4" aria-hidden="true" />
                {loadingMessage}
              </div>
            )}
            {!loading && results.length === 0 && (
              <CommandEmpty>{emptyMessage}</CommandEmpty>
            )}
            {!loading && results.length > 0 && (
              <CommandGroup>
                {results.map((r) => (
                  <CommandItem
                    key={String(r.value)}
                    value={String(r.value)}
                    onSelect={() => {
                      onSelect(r);
                      setQuery('');
                      setOpen(false);
                    }}
                    className="data-selected:bg-blue-100 data-selected:text-blue-700"
                  >
                    <div className="flex flex-col min-w-0">
                      {renderResult ? (
                        renderResult(r)
                      ) : (
                        <>
                          <span className="truncate text-sm text-grey-700">{r.label}</span>
                          {r.description && (
                            <span className="truncate text-sm text-grey-600">
                              {r.description}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        )}
      </Command>
    </div>
  );
}
